import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Upload, Download, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

type ImportExportBarProps = {
    templates: any[];
    onImportComplete: () => void;
    filteredTemplates?: any[];
    getSortedData?: (data: any[]) => any[];
    getOrgName?: (template: any) => string;
    getGroupName?: (template: any) => string;
    t: any; // Accept any function signature for translation
};

export const ImportExportBar = ({ templates, filteredTemplates, onImportComplete, getSortedData, getOrgName, getGroupName, t }: ImportExportBarProps) => {
    const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
    const [csvFile, setCsvFile] = useState<File | null>(null);
    const [csvData, setCsvData] = useState<string[][]>([]);
    const [columnMappings, setColumnMappings] = useState<Record<string, number>>({});

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target || !e.target.files || !e.target.files[0]) {
            toast.error(t("toasts.no_file", "No file selected"));
            return;
        }

        const file = e.target.files[0];
        setCsvFile(file);

        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                if (!event.target || !event.target.result) {
                    throw new Error("Failed to read file");
                }

                const csvText = event.target.result as string;
                if (!csvText || csvText.trim() === '') {
                    throw new Error("File is empty");
                }

                // More robust CSV parsing with error handling
                const lines = csvText.split(/\r?\n/).filter(line => line.trim() !== '');

                if (lines.length === 0) {
                    throw new Error("No data found in file");
                }

                const parsedRows: string[][] = [];

                for (const line of lines) {
                    // Handle quoted values correctly
                    let inQuote = false;
                    let currentValue = '';
                    const row: string[] = [];

                    for (let i = 0; i < line.length; i++) {
                        const char = line[i];

                        if (char === '"') {
                            inQuote = !inQuote;
                        } else if (char === ',' && !inQuote) {
                            row.push(currentValue.trim());
                            currentValue = '';
                        } else {
                            currentValue += char;
                        }
                    }

                    // Add the last value
                    row.push(currentValue.trim());

                    // Only add non-empty rows
                    if (row.some(cell => cell.trim() !== '')) {
                        parsedRows.push(row);
                    }
                }

                if (parsedRows.length === 0) {
                    throw new Error("No valid data rows found");
                }

                setCsvData(parsedRows);

                // Auto-detect column mappings based on header row
                if (parsedRows.length > 0 && parsedRows[0]?.length > 0) {
                    const headers = parsedRows[0].map(h => (h || '').toLowerCase());
                    const mappings: Record<string, number> = {};

                    // Try to auto-map columns
                    headers.forEach((header, index) => {
                        if (!header) return;

                        if (header.includes('name')) mappings['recipient_name'] = index;
                        if (header.includes('type')) mappings['template_type'] = index;
                        if (header.includes('org')) mappings['organization'] = index;
                        if (header.includes('group')) mappings['group_id'] = index;
                        if (header.includes('email')) mappings['recipient_email'] = index;
                        if (header.includes('phone')) mappings['client_phone'] = index;
                        if (header.includes('tax')) mappings['client_tax_id'] = index;
                        if (header.includes('address')) mappings['recipient_address'] = index;
                    });

                    setColumnMappings(mappings);
                } else {
                    throw new Error("Could not parse headers from CSV");
                }
            } catch (error: any) {
                console.error("CSV parsing error:", error);
                toast.error(t("toasts.parsing_error", `Error parsing CSV file: ${error?.message || "Unknown error"}`));
                setCsvFile(null);
                setCsvData([]);
            }
        };

        reader.onerror = (error) => {
            console.error("File reading error:", error);
            toast.error(t("toasts.file_read_error", "Error reading file"));
            setCsvFile(null);
            setCsvData([]);
        };

        reader.readAsText(file);
    };

    const handleImport = async () => {
        console.log("Importing")
        try {
            if (!csvData || csvData.length <= 1) {
                toast.error(t("toasts.no_data", "No data to import"));
                return;
            }

            console.log("Handling Import")
            console.log(csvData)

            // Validate required mappings
            const requiredFields = ['recipient_name', 'recipient_email'];
            const missingFields = requiredFields.filter(field => columnMappings[field] === undefined);

            console.log("Missing Fields")
            console.log(missingFields)
            if (missingFields.length > 0) {
                toast.error(t("toasts.missing_required_fields", "Missing required field mappings: ") + missingFields.join(', '));
                return;
            }



            // Skip header row
            const dataRows = csvData.slice(1);
            const importData = [];

            for (const row of dataRows) {
                if (!row || row.length <= 1) continue; // Skip empty rows

                const templateData: any = {
                    template_type: 'business', // Default value
                };

                Object.entries(columnMappings).forEach(([field, columnIndex]) => {
                    if (columnIndex !== undefined && columnIndex < row.length) {
                        const value = row[columnIndex];
                        if (value && value.trim() !== '') {
                            templateData[field] = value.trim();
                        }
                    }
                });

                // Validate required fields
                if (!templateData.recipient_name || !templateData.recipient_email) {
                    continue; // Skip rows with missing required data
                }

                // Clean up template_type - with safe fallback
                if (templateData.template_type) {
                    const type = String(templateData.template_type).toLowerCase();
                    templateData.template_type = type.includes('business') || type.includes('company') ? 'business' : 'individual';
                }

                importData.push(templateData);
            }

            if (importData.length === 0) {
                toast.error(t("toasts.no_valid_data", "No valid data to import"));
                return;
            }

            // Get current user
            const { data } = await supabase.auth.getUser();
            const user = data?.user;

            if (!user) {
                toast.error(t("toasts.auth_error", "Authentication error. Please sign in again."));
                return;
            }

            // Add user_id to each record
            importData.forEach(record => {
                record.user_id = user.id;
                record.name = record.recipient_name;
            });

            // Insert data in batches
            const batchSize = 100;
            let successCount = 0;

            for (let i = 0; i < importData.length; i += batchSize) {
                const batch = importData.slice(i, i + batchSize);
                const { error, count } = await supabase
                    .from("recipient_templates")
                    .insert(batch)
                    .select();

                if (error) throw error;
                successCount += count || 0;
            }

            setIsImportDialogOpen(false);
            setCsvFile(null);
            setCsvData([]);
            setColumnMappings({});

            // Call the callback to refresh the data
            onImportComplete();

            toast.success(t("toasts.import_success", `Successfully imported ${successCount} customers`));
        } catch (error: any) {
            console.error("Import error:", error);
            toast.error(t("toasts.import_error", "Import failed: ") + (error?.message || "Unknown error"));
        }
    };

    // CSV export function
    const handleExport = async () => {
        try {
            const { data: { session }, error: sessionError } = await supabase.auth.getSession();

            if (sessionError || !session) {
                toast.error(t("errors.notAuthenticated"));
                return;
            }

            // Fetch the latest data from the database with the current user_id filter
            const { data: exportData, error: fetchError } = await supabase
                .from("recipient_templates")
                .select('*')
                .eq("user_id", session.user.id)
                .order("created_at", { ascending: false });

            if (fetchError) {
                throw fetchError;
            }

            if (!exportData || exportData.length === 0) {
                toast.error(t("errors.noDataToExport", "No data available to export"));
                return;
            }

            // Use filtered templates if available, otherwise use all templates
            let dataToExport = exportData;
            if (filteredTemplates && filteredTemplates.length > 0) {
                // If we're viewing filtered data, only export those records
                // by matching IDs from the filtered templates
                const filteredIds = new Set(filteredTemplates.map(t => t.id));
                dataToExport = exportData.filter(template => filteredIds.has(template.id));
            }

            // Use getSortedData if provided to sort the data
            const sortedData = getSortedData ? getSortedData(dataToExport) : dataToExport;

            if (sortedData.length === 0) {
                toast.error(t("errors.noDataToExport", "No data available to export"));
                return;
            }

            // Create CSV header with all relevant fields
            const headers = [
                "Name",
                "Email",
                "Type",
                "Phone",
                "Tax ID",
                "Address",
                "Created At"
            ];

            // Create CSV rows
            const rows = sortedData.map((template) => [
                template.recipient_name || "",
                template.recipient_email || "",
                template.template_type || "",
                template.client_phone || "",
                template.client_tax_id || "",
                template.recipient_address || "",
                new Date(template.created_at).toLocaleDateString(),
            ]);

            // Combine headers and rows
            const csvContent = [headers, ...rows]
                .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
                .join("\n");

            // Create and download the CSV file
            const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.setAttribute("href", url);
            link.setAttribute("download", `customers-${new Date().toISOString().slice(0, 10)}.csv`);
            link.style.visibility = "hidden";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            toast.success(t("exportSuccess", "Export completed successfully"));
        } catch (error: any) {
            console.error("Export error:", error);
            toast.error(t("errors.exportFailed", "Export failed") + (error?.message ? `: ${error.message}` : ""));
        }
    };

    return (
        <>
            <Button
                variant="outline"
                className="gap-1"
                onClick={handleExport}
            >
                <Download className="h-4 w-4" />
                {t("customers.export", "Export")}
            </Button>

            <Button
                variant="outline"
                className="gap-1"
                onClick={() => setIsImportDialogOpen(true)}
            >
                <Upload className="h-4 w-4" />
                {t("customers.import", "Import")}
            </Button>

            {/* Import Dialog */}
            <Dialog open={isImportDialogOpen} onOpenChange={setIsImportDialogOpen}>
                <DialogContent className="w-[95vw] max-w-3xl md:max-w-4xl overflow-y-auto max-h-[90vh]">
                    <DialogHeader>
                        <DialogTitle className="text-[#34375B]">
                            {t("customers.import_csv", "Import Customers from CSV")}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4">
                        {!csvFile ? (
                            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                                <Upload className="h-8 w-8 mx-auto mb-4 text-gray-400" />
                                <p className="mb-2 text-sm text-gray-500">
                                    {t("customers.drag_drop", "Drag and drop your CSV file here, or")}
                                </p>
                                <Input
                                    id="csvFile"
                                    type="file"
                                    accept=".csv"
                                    onChange={handleFileChange}
                                    className="hidden"
                                />
                                <Button
                                    variant="outline"
                                    onClick={() => document.getElementById('csvFile')?.click()}
                                >
                                    {t("customers.browse_files", "Browse files")}
                                </Button>
                            </div>
                        ) : (
                            <>
                                <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                                    <div>
                                        <p className="text-sm font-thin">{csvFile.name}</p>
                                        <p className="text-sm text-gray-500">
                                            {csvData.length > 0 ? t("customers.records_found", `${csvData.length - 1} records found`, { count: csvData.length - 1 }) : t("common.is_loading", "Processing...")}
                                        </p>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                            setCsvFile(null);
                                            setCsvData([]);
                                            setColumnMappings({});
                                        }}
                                    >
                                        <X className="h-4 w-4" />
                                    </Button>
                                </div>

                                {csvData.length > 0 && csvData[0] && (
                                    <>
                                        <div className="space-y-4">
                                            <h3 className="text-lg font-xl">
                                                {t("customers.map_columns", "Map CSV Columns")}
                                            </h3>
                                            <p className="text-sm font-thin text-gray-500">
                                                {t("customers.map_instructions", "Match your CSV columns to the correct customer fields. Required fields are marked with *.")}
                                            </p>

                                            <div className="space-y-3">
                                                {/* Column mappings */}
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="text-[#8791F3] text-sm font-medium mb-2 block">
                                                            {t("templates.recipient_name", "Customer Name")} *
                                                        </label>
                                                        <Select
                                                            value={columnMappings.recipient_name?.toString()}
                                                            onValueChange={(value) => setColumnMappings({ ...columnMappings, recipient_name: parseInt(value) })}
                                                        >
                                                            <SelectTrigger className="text-[#34375B] font-normal bg-white border border-gray-300 p-2 rounded-md">
                                                                <SelectValue placeholder={t("customers.select_column", "Select column")} />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {csvData[0]?.map((header, index) => (
                                                                    <SelectItem key={index} value={index.toString()}>
                                                                        {header || ''}
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                    </div>

                                                    <div>
                                                        <label className="text-[#8791F3] text-sm font-medium mb-2 block">
                                                            {t("templates.type", "Type")} *
                                                        </label>
                                                        <Select
                                                            value={columnMappings.template_type?.toString()}
                                                            onValueChange={(value) => setColumnMappings({ ...columnMappings, template_type: parseInt(value) })}
                                                        >
                                                            <SelectTrigger className="text-[#34375B] font-normal bg-white border border-gray-300 p-2 rounded-md">
                                                                <SelectValue placeholder={t("customers.select_column", "Select column")} />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {csvData[0]?.map((header, index) => (
                                                                    <SelectItem key={index} value={index.toString()}>
                                                                        {header || ''}
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="text-[#8791F3] text-sm font-medium mb-2 block">
                                                            {t("customers.organization", "Organization")}
                                                        </label>
                                                        <Select
                                                            value={columnMappings.organization?.toString()}
                                                            onValueChange={(value) => setColumnMappings({ ...columnMappings, organization: parseInt(value) })}
                                                        >
                                                            <SelectTrigger className="text-[#34375B] font-normal bg-white border border-gray-300 p-2 rounded-md">
                                                                <SelectValue placeholder={t("customers.select_column_optional", "Select column (optional)")} />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {csvData[0]?.map((header, index) => (
                                                                    <SelectItem key={index} value={index.toString()}>
                                                                        {header || ''}
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                    </div>

                                                    <div>
                                                        <label className="text-[#8791F3] text-sm font-medium mb-2 block">
                                                            {t("customers.group", "Group")}
                                                        </label>
                                                        <Select
                                                            value={columnMappings.group_id?.toString()}
                                                            onValueChange={(value) => setColumnMappings({ ...columnMappings, group_id: parseInt(value) })}
                                                        >
                                                            <SelectTrigger className="text-[#34375B] font-normal bg-white border border-gray-300 p-2 rounded-md">
                                                                <SelectValue placeholder={t("customers.select_column_optional", "Select column (optional)")} />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {csvData[0]?.map((header, index) => (
                                                                    <SelectItem key={index} value={index.toString()}>
                                                                        {header || ''}
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="text-[#8791F3] text-sm font-medium mb-2 block">
                                                            {t("templates.email", "Email")} *
                                                        </label>
                                                        <Select
                                                            value={columnMappings.recipient_email?.toString()}
                                                            onValueChange={(value) => setColumnMappings({ ...columnMappings, recipient_email: parseInt(value) })}
                                                        >
                                                            <SelectTrigger className="text-[#34375B] font-normal bg-white border border-gray-300 p-2 rounded-md">
                                                                <SelectValue placeholder={t("customers.select_column", "Select column")} />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {csvData[0]?.map((header, index) => (
                                                                    <SelectItem key={index} value={index.toString()}>
                                                                        {header || ''}
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                    </div>

                                                    <div>
                                                        <label className="text-[#8791F3] text-sm font-medium mb-2 block">
                                                            {t("templates.phone", "Phone")}
                                                        </label>
                                                        <Select
                                                            value={columnMappings.client_phone?.toString()}
                                                            onValueChange={(value) => setColumnMappings({ ...columnMappings, client_phone: parseInt(value) })}
                                                        >
                                                            <SelectTrigger className="text-[#34375B] font-normal bg-white border border-gray-300 p-2 rounded-md">
                                                                <SelectValue placeholder={t("customers.select_column_optional", "Select column (optional)")} />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {csvData[0]?.map((header, index) => (
                                                                    <SelectItem key={index} value={index.toString()}>
                                                                        {header || ''}
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="text-[#8791F3] text-sm font-medium mb-2 block">
                                                            {t("templates.tax_id", "Tax ID")}
                                                        </label>
                                                        <Select
                                                            value={columnMappings.client_tax_id?.toString()}
                                                            onValueChange={(value) => setColumnMappings({ ...columnMappings, client_tax_id: parseInt(value) })}
                                                        >
                                                            <SelectTrigger className="text-[#34375B] font-normal bg-white border border-gray-300 p-2 rounded-md">
                                                                <SelectValue placeholder={t("customers.select_column_optional", "Select column (optional)")} />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {csvData[0]?.map((header, index) => (
                                                                    <SelectItem key={index} value={index.toString()}>
                                                                        {header || ''}
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                    </div>

                                                    <div>
                                                        <label className="text-[#8791F3] text-sm font-medium mb-2 block">
                                                            {t("templates.address", "Address")}
                                                        </label>
                                                        <Select
                                                            value={columnMappings.recipient_address?.toString()}
                                                            onValueChange={(value) => setColumnMappings({ ...columnMappings, recipient_address: parseInt(value) })}
                                                        >
                                                            <SelectTrigger className="text-[#34375B] font-normal bg-white border border-gray-300 p-2 rounded-md">
                                                                <SelectValue placeholder={t("customers.select_column_optional", "Select column (optional)")} />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {csvData[0]?.map((header, index) => (
                                                                    <SelectItem key={index} value={index.toString()}>
                                                                        {header || ''}
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* CSV Preview */}
                                            {csvData.length > 1 && csvData[0] && (
                                                <div className="border rounded-md mt-4">
                                                    <div className="bg-gray-50 p-3">
                                                        <h4 className="font-medium">
                                                            {t("customers.preview", "Preview")}
                                                        </h4>
                                                    </div>
                                                    <div className="overflow-x-auto" style={{ maxWidth: '100%' }}>
                                                        {csvData[0] && (
                                                            <table className="w-full text-sm" style={{ minWidth: '100%' }}>
                                                                <thead>
                                                                    <tr className="bg-gray-50">
                                                                        {csvData[0]?.map((header, index) => (
                                                                            <th key={index} className="px-4 py-2 text-left font-medium">
                                                                                {header || ''}
                                                                            </th>
                                                                        ))}
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {csvData.slice(1, 4).filter(Boolean).map((row, rowIndex) => (
                                                                        <tr key={rowIndex} className="border-t">
                                                                            {row.map((cell, cellIndex) => (
                                                                                <td key={cellIndex} className="px-4 py-2">
                                                                                    {cell || ''}
                                                                                </td>
                                                                            ))}
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        )}
                                                    </div>
                                                    {csvData.length > 4 && (
                                                        <div className="p-2 text-center text-sm text-gray-500">
                                                            {t("customers.preview_more", "And {count} more rows", { count: csvData.length - 4 })}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        <div className="flex justify-end gap-2">
                                            <Button variant="outline" onClick={() => setIsImportDialogOpen(false)}>
                                                {t("common.cancel", "Cancel")}
                                            </Button>
                                            <Button
                                                className="bg-[#8791F3] hover:bg-[#5CC2BB] hover:text-[#34375B]"
                                                onClick={handleImport}
                                            >
                                                <Upload className="h-4 w-4 mr-2" />
                                                {t("customers.import", "Import")}
                                            </Button>
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}; 